import { toast } from "react-toastify";
import { Mixpanel, MxEvent } from "../Mixpanel/Mixpanel";
import { apiGet, apiPatch, apiPost, apiPostImageOnPresignedData } from "../components/api";
import {
  CURRENT_USER,
  GET_IMAGE_API_URL,
  LF_DEBUGGER,
  LF_DEBUGGER_HUBSPOT,
  LOGIN_URL,
  RESTRICTED_EMAILS,
  SAVE_IMAGE_API_URL,
  USER_DETAILS_API_URL,
} from "./constants";
import * as Sentry from '@sentry/react';

export const getUserDetails = async (_token) => {
  const token = _token || getDataFromSearch()?.token
  if (token)
    localStorage.setItem("token", token);

  const userDetails = await apiGet(USER_DETAILS_API_URL, true);

  if (userDetails?.id) {
    const { id, first_name, last_name, email, organization_details, role_details } =
      userDetails || {};

    const name = `${first_name} ${last_name}`.trim();

    const data = {
      user: {
        id,
        first_name,
        last_name,
        name,
        email,
      },
      organization_detail: {
        id: organization_details?.id,
        name: organization_details?.name,
      },
      isGuestUser : !role_details,

    };

    Sentry.setUser({
        id,
        name,
        email,
        organization: organization_details?.name,
        org_id: organization_details?.id
    })
    localStorage.setItem(CURRENT_USER, JSON.stringify(data));

    Mixpanel.reset();
    Mixpanel.identify(id);

    Mixpanel.people.set({
      $first_name: first_name,
      $last_name: last_name,
      full_name: name,
      email: email,
    });

    if (!window.location.pathname.includes("image-gen")) {
      Mixpanel.track({
        eventName: MxEvent.VISITED_HOME,
      });
    }

    window.history.pushState(null, "", `${window.location.pathname}`);
  } else {
    Mixpanel.reset();
    localStorage.clear();
    Sentry.captureException("We're unable to verify you. Kindly proceed by logging in.")
    captureSentryException({failed_reason : "We're unable to verify you. Kindly proceed by logging in."})
    toast.error("We're unable to verify you. Kindly proceed by logging in.", {
      closeOnClick: true,
      closeButton: false,
    });
    setTimeout(() => {
      window.open(LOGIN_URL, "_self");
    }, 1000);
  }
};

export const getSearch = (value) => {
  const search = window.location.search
    .split("&")
    .find((_item) => _item.includes(value));

  return search?.split(value)?.at(-1) || null;
};


export const isHubspotDebuggingEnabled = () =>
  localStorage.getItem(LF_DEBUGGER_HUBSPOT) === "true";

const enableDisabledButton = ({ value = "", button = null, email = null }) => {
  if (!button || !email) return;

  const debugMessage = `<ul style="padding: 6px 2px" class="no-list hs-error-msgs inputs-list" role="alert"><li><label class="hs-error-msg hs-main-font-element">You are an internal user. To submit your email, please enable debugging mode by clicking <a href="${window.location.origin + window.location.pathname
    }?${LF_DEBUGGER}=1">here</a>.</label></li></ul>`;

  const isExists = value && RESTRICTED_EMAILS.some((a) => value?.includes(a));
  if (!isHubspotDebuggingEnabled() && isExists) {
    button.disabled = true;
    button.style.opacity = 0.5;
    button.style.cursor = "not-allowed";
    if (email) {
      email?.nextElementSibling?.remove();
      email.insertAdjacentHTML("afterend", debugMessage);
    }
  } else {
    button.disabled = false;
    button.style.opacity = 1;
    button.style.cursor = "pointer";
    email?.nextElementSibling?.remove();
  }
};

export const preventFormSubmissionForInternalUsers = () => {
  const iframes = document.querySelectorAll(".hs-form-iframe");
  iframes.forEach((iframe) => {
    const forms =
      iframe?.contentWindow?.document?.body?.querySelectorAll("form");
    forms.forEach((form) => {
      const dataFormId = form.getAttribute("data-form-id");
      if (form.id === `hsForm_${dataFormId}`) {
        const email = form.querySelector(`#email-${dataFormId}`);
        const button = form.querySelector("input[type='submit']");

        enableDisabledButton({
          value: email?.value,
          button,
          email,
        });

        email.addEventListener(
          "input",
          (e) => {
            const value = e.target.value;
            enableDisabledButton({
              value,
              button,
              email,
            });
          },
          true
        );
      }
    });
  });
};

export const getBase64 = (file, callback) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    const base64String = reader.result.split(",")[1];
    callback("data:image/png;base64," + base64String);
  };
  reader.readAsDataURL(file);
};

export const getBase64FromBlob = async (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const convertBase64ToBlob = (base64) => {
  if (!base64) return null;
  // Remove data URI scheme
  var base64Data = base64.split(",")[1];
  // console.log(base64Data);
  // Decode Base64 to binary data
  var binaryData = atob(base64Data);

  // Convert binary data to Blob
  var blob = new Blob([binaryData], { type: "application/octet-stream" });

  return blob;
};

export const validateUser = async (setPageLoading) => {
  setPageLoading(true);
  await getUserDetails();
  setPageLoading(false);
};


export const getDataFromSearch = () => {
  const search = window.location.search?.split("?")?.[1]?.split("&") || [];

  let allSearch = {}

  search.forEach(s => {
    allSearch[s.split("=")[0]] = decodeURIComponent(s.split("=")[1])
  })

  return allSearch
}

export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const user = localStorage.getItem(CURRENT_USER);
    if (!user) {
      resolve(null);
      return;
    }
    resolve(JSON.parse(user));
  });
};

export const sessionDestroy = () => {
  Mixpanel.reset();
  localStorage.clear();
  captureSentryException({failed_reason : "We're unable to verify you. Kindly proceed by logging in."})
  toast.error("We're unable to verify you. Kindly proceed by logging in.", {
    closeOnClick: true,
    closeButton: false,
  });
  setTimeout(() => {
    window.open(LOGIN_URL, "_self");
  }, 1000);
};

export async function blobUrlToBlob(blobUrl) {
  // Fetch the Blob from the URL
  const response = await fetch(blobUrl);

  // Ensure the response is successful
  if (!response.ok) {
    captureSentryException({failed_reason : `Failed to fetch the Blob from the URL: ${response.statusText}`})
    throw new Error(
      `Failed to fetch the Blob from the URL: ${response.statusText}`
    );
  }

  // Get the Blob from the response
  const blob = await response.blob();
  return blob;
}

export function canvasToBlobAndAppend(canvas, formData, fieldName) {
  return new Promise(function (resolve, reject) {
    canvas.toBlob(function (blob) {
      if (blob) {
        if (formData) formData.append(fieldName, blob);
        resolve(blob);
      } else {
        reject("Failed to convert canvas to blob");
      }
    });
  });
}

const generateThumbnail = (image) => {
  // Create a new promise
  return new Promise((resolve, reject) => {
    // Create canvas and context
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Call upscaleImage and wait for the result

    const img = new Image();
    img.src = image;

    // Set up a load event listener for the image
    img.onload = async function () {
      const imageWidth = img.width
      const imageHeight = img.height
      const aspectRatio = imageWidth / imageHeight

      // Set the canvas dimensions to your custom resolution
      canvas.width = 544;
      canvas.height = 544 / aspectRatio;

      // Keep quality of image
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convert the canvas to a Blob
      const blob = await canvasToBlobAndAppend(canvas);
      resolve(blob); // Resolve the promise with the blob
    };

    img.onerror = (error) => {
      captureSentryException({failed_reason : "Failed to load image"})
      reject(new Error("Failed to load image"));
    };
  });
}

export const uploadImageToApp = async ({ data, input, output, mask, reference }) => {
  const uploadImageResponse = await apiPost(SAVE_IMAGE_API_URL, data, true)

  if (uploadImageResponse?.status) {
    const _data = uploadImageResponse?.data?.[0]
    const thumbnail = await generateThumbnail(URL.createObjectURL(output))

    if (thumbnail)
      apiPostImageOnPresignedData(thumbnail, _data?.thumb_presigned_data)

    if (input)
      apiPostImageOnPresignedData(input, _data?.input_image_presigned_data)

    if (mask)
      apiPostImageOnPresignedData(mask, _data?.input_mask_image_presigned_data)

    if (reference)
      apiPostImageOnPresignedData(reference, _data?.reference_image_presigned_data)

    const awsUploadOutputStatus = await apiPostImageOnPresignedData(output, _data?.output_image_presigned_data)

    if (Array.from({ length: 6 }, (_, i) => i + 200)?.includes(+awsUploadOutputStatus?.status)) {
      const aiImageDetailUrl = `${GET_IMAGE_API_URL}${_data?.id}/`
      const _formData = new FormData()
      _formData.append("is_visible", true)
      apiPatch(aiImageDetailUrl, _formData, true)
    }
  }
}

export const convertTransparentToBlackAndNonTransparentToWhite = async (image) => {
  return await new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const img = new Image();
    img.src = image;

    img.onload = async () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];

        if (alpha === 0) {
          // Transparent pixel: Set to black
          data[i] = 0;
          data[i + 1] = 0;
          data[i + 2] = 0;
          data[i + 3] = 255;
        } else {
          // Non-transparent pixel: Set to white
          data[i] = 255;
          data[i + 1] = 255;
          data[i + 2] = 255;
          data[i + 3] = 255;
        }
      }

      ctx.putImageData(imageData, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
          document.body.appendChild(canvas);
          document.body.removeChild(canvas);
        } else {
          captureSentryException({failed_reason : "Failed to convert canvas to Blob"})
          reject(new Error("Failed to convert canvas to Blob"));
        }
      });
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };
  });
};

export const captureSentryException = (_data, otherInfo, file) => {
  const user = JSON.parse(localStorage.getItem(CURRENT_USER))

  let data = {
    ..._data,
    user_id: user?.id,
    org_id: user?.organization,
    org_name: user?.organization_detail?.name,
    user_email: user?.user?.email,
    user_name: user?.user?.name,
  }

  console.log("sentry", data)
  Sentry.captureException(data, otherInfo)
}